/** Internal forms helpers (pure; InternalForms.tsx renders them). KB jotform.md. */
import { useEffect, useState } from 'react';
import { fetchInternalForms, type InternalForm } from '../services/kbApi';

/** Forms matching every word of the query (title or workspace). */
export function matchForms(forms: InternalForm[], query: string): InternalForm[] {
  const words = query.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return words.length ? forms.filter((f) => words.every((w) => `${f.title} ${f.workspace}`.toLowerCase().includes(w))) : forms;
}

export const lupaFormPrompt = (f: InternalForm) =>
  `Help me fill out the "${f.title}" form (${f.workspace}). Ask me what you need, then give me the pre-filled link.`;

/** "Lands in Teamwork: Project → Task list", or that it isn't recorded yet. */
export function teamworkLine(f: InternalForm): string {
  const tw = f.teamwork;
  if (tw?.project) return `Lands in Teamwork: ${tw.project}${tw.task_list ? ` → ${tw.task_list}` : ''}`;
  return 'Where it lands in Teamwork: not recorded yet';
}

// One shared load for the sidebar and the forms list (refetched after 5 minutes).

let shared: { at: number; promise: Promise<InternalForm[]> } | null = null;

function loadForms(): Promise<InternalForm[]> {
  if (!shared || Date.now() - shared.at > 5 * 60 * 1000) {
    const promise = fetchInternalForms().then((r) => r.forms);
    shared = { at: Date.now(), promise };
    promise.catch(() => {
      shared = null;
    });
  }
  return shared.promise;
}

/** Internal forms, or null while loading; `error` when JotForm is unavailable. */
export function useInternalForms(): { forms: InternalForm[] | null; error: boolean } {
  const [state, setState] = useState<{ forms: InternalForm[] | null; error: boolean }>({ forms: null, error: false });
  useEffect(() => {
    let cancelled = false;
    loadForms()
      .then((forms) => !cancelled && setState({ forms, error: false }))
      .catch(() => !cancelled && setState({ forms: null, error: true }));
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

export function groupByWorkspace(forms: InternalForm[]): [string, InternalForm[]][] {
  const out: Record<string, InternalForm[]> = {};
  for (const f of forms) (out[f.workspace] ??= []).push(f);
  return Object.entries(out);
}
